import type { Position } from "geojson";

/** Euclidean distance between two positions */
function euclideanDistance(a: Position, b: Position): number {
	const dx = a[0] - b[0];
	const dy = a[1] - b[1];
	return Math.sqrt(dx * dx + dy * dy);
}

/** Find the index of the closest point in a ring to the target */
function findClosestPointIndex(ring: Position[], target: Position): number {
	let minDist = Infinity;
	let minIdx = 0;
	for (let i = 0; i < ring.length; i++) {
		const d = euclideanDistance(ring[i], target);
		if (d < minDist) {
			minDist = d;
			minIdx = i;
		}
	}
	return minIdx;
}

function coordsEqual(a: Position, b: Position): boolean {
	return a[0] === b[0] && a[1] === b[1];
}

/**
 * Remove a section of a polygon ring between two anchor points.
 *
 * Given polygon rings, finds the ring closest to `inBetweenPoint`, then removes
 * the arc between `firstPoint` and `lastPoint` that contains `inBetweenPoint`.
 *
 * @param rings Array of rings (outer + holes) as Position[][]
 * @param firstPoint One end of the section to erase
 * @param inBetweenPoint A point on the arc to erase (determines which arc)
 * @param lastPoint Other end of the section to erase
 * @returns New rings with the section erased, or undefined if invalid
 */
export function eraseSection(
	rings: Position[][],
	firstPoint: Position,
	inBetweenPoint: Position,
	lastPoint: Position,
): Position[][] | undefined {
	// Find the ring (can be outer or hole) closest to inBetweenPoint
	let targetRingIndex = 0;
	let minDistance = Infinity;

	for (let i = 0; i < rings.length; i++) {
		const ring = rings[i];
		const closestIdx = findClosestPointIndex(ring, inBetweenPoint);
		const d = euclideanDistance(ring[closestIdx], inBetweenPoint);
		if (d < minDistance) {
			minDistance = d;
			targetRingIndex = i;
		}
	}

	const targetRing = rings[targetRingIndex];

	// Find indices in that ring (closest vertex indices)
	const firstIndex = findClosestPointIndex(targetRing, firstPoint);
	const lastIndex = findClosestPointIndex(targetRing, lastPoint);
	const inBetweenIndex = findClosestPointIndex(targetRing, inBetweenPoint);

	const newRing = removePolygonSection(targetRing, firstIndex, inBetweenIndex, lastIndex);

	if (!newRing) return undefined;

	return rings.map((ring, idx) => (idx === targetRingIndex ? newRing : ring));
}

/**
 * Remove the section of a ring that contains inBetweenIndex.
 * Returns a properly closed ring with at least 3 distinct vertices, or undefined.
 */
function removePolygonSection(
	ring: Position[],
	firstIndex: number,
	inBetweenIndex: number,
	lastIndex: number,
): Position[] | undefined {
	// If ring is too small, bail out, need at least 3 + closing
	if (!ring || ring.length < 4) return undefined;

	// Remove closing duplicate if present
	const isClosed = coordsEqual(ring[0], ring[ring.length - 1]);
	const base = isClosed ? ring.slice(0, -1) : ring.slice();
	const N = base.length;

	if (N < 3) return undefined;

	// Normalize incoming indices into [0..N-1]
	const norm = (idx: number) => ((idx % N) + N) % N;
	const A = norm(firstIndex);
	const B = norm(lastIndex);
	const X = norm(inBetweenIndex);

	// If first and last are the same vertex, nothing to remove
	if (A === B) return undefined;

	// Adjacent check: if A and B adjacent in circular sense - nothing to remove
	if ((A + 1) % N === B || (B + 1) % N === A) return undefined;

	// Is X in the arc from A to B going forward?
	const inArc = (from: number, x: number, to: number) => {
		if (from < to) return x > from && x < to;
		return x > from || x < to;
	};

	const xInDirect = inArc(A, X, B);

	// Build the kept-index list depending on which arc contains X
	const keepIndices: number[] = [];

	if (xInDirect) {
		// Remove A->B arc, keep B->A arc
		let i = B;
		do {
			keepIndices.push(i);
			i = (i + 1) % N;
		} while (i !== A);
		keepIndices.push(A);
	} else {
		// Remove B->A arc, keep A->B arc
		let i = A;
		do {
			keepIndices.push(i);
			i = (i + 1) % N;
		} while (i !== B);
		keepIndices.push(B);
	}

	// Rebuild coordinates from kept indices
	const newCoords = keepIndices.map((idx) => base[idx]);

	// Validate result must have at least 3 distinct vertices
	if (newCoords.length < 3) return undefined;

	// Ensure closure
	if (!coordsEqual(newCoords[0], newCoords[newCoords.length - 1])) {
		newCoords.push([...newCoords[0]]);
	}

	// After closure, final length must be >= 4 (3 distinct verts + closing)
	if (newCoords.length < 4) return undefined;

	return newCoords;
}
